import React, { useEffect, useState } from "react";
import { PaymentForm, CreditCard, ApplePay } from "react-square-web-payments-sdk";
import { createSquarePayment } from "../services/apis";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";

// Country Square's payment request expects for each supported currency
const CURRENCY_COUNTRY: Record<string, string> = {
  CAD: "CA",
  USD: "US",
  GBP: "GB",
  AUD: "AU",
  EUR: "IE",
  JPY: "JP",
};
const DEFAULT_PAYMENT_ERROR =
  "Payment could not be processed. Please try again or use a different card.";

// Friendly messages for Square error codes
const SQUARE_ERROR_MESSAGES: Record<string, string> = {
  CARD_DECLINED: "Your card was declined.",
  GENERIC_DECLINE: "Your card was declined.",
  CARD_DECLINED_CALL_ISSUER: "Your card was declined. Please contact your bank or use a different card.",
  CARD_DECLINED_VERIFICATION_REQUIRED: "Your card requires verification. Please try again or use a different card.",
  INSUFFICIENT_FUNDS: "Insufficient funds. Please use a different card.",
  CVV_FAILURE: "The CVV is incorrect. Please check and try again.",
  INVALID_CVV: "The CVV is incorrect. Please check and try again.",
  ADDRESS_VERIFICATION_FAILURE: "The postal code doesn't match your card. Please check and try again.",
  INVALID_POSTAL_CODE: "The postal code is invalid. Please check and try again.",
  INVALID_EXPIRATION: "The expiration date is invalid. Please check and try again.",
  INVALID_EXPIRATION_DATE: "The expiration date is invalid. Please check and try again.",
  EXPIRATION_FAILURE: "Your card has expired. Please use a different card.",
  INVALID_CARD: "The card details are invalid. Please check and try again.",
  INVALID_CARD_DATA: "The card details are invalid. Please check and try again.",
  CARD_EXPIRED: "Your card has expired. Please use a different card.",
  CARD_NOT_SUPPORTED: "This card type isn't supported. Please use a different card.",
  PAN_FAILURE: "The card number is invalid. Please check and try again.",
  INVALID_ACCOUNT: "This card can't be charged. Please use a different card.",
  TRANSACTION_LIMIT: "This payment exceeds your card's limit. Please use a different card.",
  AMOUNT_TOO_HIGH: "This payment exceeds your card's limit. Please use a different card.",
  VOICE_FAILURE: "Your card was declined. Please contact your bank or use a different card.",
  ALLOWABLE_PIN_TRIES_EXCEEDED: "Too many attempts. Please use a different card.",
  CARD_TOKEN_EXPIRED: "Your payment session expired. Please re-enter your card details.",
  CARD_TOKEN_USED: "Your payment session expired. Please re-enter your card details.",
  TEMPORARY_ERROR: "A temporary error occurred. Please try again.",
  RATE_LIMITED: "Too many attempts. Please wait a moment and try again.",
};

// Square errors arrive as raw JSON (often inside "Status code: 400 Body: {...}");
// pull out the error code and show a friendly message instead
const getFriendlyPaymentError = (raw: unknown): string => {
  if (!raw) return DEFAULT_PAYMENT_ERROR;

  let errors: any[] | undefined;
  if (typeof raw === "object") {
    const obj = raw as any;
    errors = Array.isArray(obj) ? obj : obj.errors || (obj.code ? [obj] : undefined);
    if (!errors && typeof obj.message === "string") return getFriendlyPaymentError(obj.message);
  } else if (typeof raw === "string") {
    const jsonStart = raw.search(/[{[]/);
    if (jsonStart === -1) {
      // Plain text message from our own backend is safe to show
      return raw.length <= 150 ? raw : DEFAULT_PAYMENT_ERROR;
    }
    try {
      const parsed = JSON.parse(raw.slice(jsonStart));
      errors = Array.isArray(parsed) ? parsed : parsed?.errors;
    } catch {
      const match = raw.match(/"code"\s*:\s*"([A-Z_]+)"/);
      if (match) errors = [{ code: match[1] }];
    }
  }

  const code = errors?.[0]?.code;
  return (code && SQUARE_ERROR_MESSAGES[code]) || DEFAULT_PAYMENT_ERROR;
};

interface SquarePaymentProps {
  pricing: number;
  handleOrder: (
    paymentMethod: string,
    userId?: string,
    transactionId?: string,
  ) => void;
  onSuccess?: () => void;
  // Runs before the card is charged; return false to stop the payment
  validateBeforePayment?: () => boolean;
  applicationId?: string | null;
  locationId?: string | null;
  hotelId?: string;
}

export default function SquarePayment({
  pricing,
  handleOrder,
  onSuccess,
  validateBeforePayment,
  applicationId: applicationIdProp,
  locationId: locationIdProp,
  hotelId,
}: SquarePaymentProps) {
  const [loading, setLoading] = useState(false);
  const { squareApplicationId, squareLocationId, currency } = useAuth();
  const applicationId = applicationIdProp ?? squareApplicationId;
  const locationId = locationIdProp ?? squareLocationId;

  useEffect(() => {
    console.log("Square credentials updated:", {
      applicationId,
      locationId,
    });
  }, [applicationId, locationId]);

  const paymentAmountCents = Math.round(pricing * 100);
  // Apple Pay / Google Pay need the merchant's currency and country:
  // use the admin-selected platform currency (e.g. CAD → CA)
  const currencyCode = (currency || "USD").toUpperCase();
  const countryCode = CURRENCY_COUNTRY[currencyCode] || "US";
  const paymentLabel = "Order Total";

  return (
    <div className="w-full bg-white dm-payment-section rounded-lg rounded-tl-none rounded-tr-none border border-black/10 border-t-0 p-4 sm:p-5">
      <PaymentForm
        key="square-payment-form"
        applicationId={applicationId!.trim()}
        locationId={locationId!.trim()}
        createPaymentRequest={() => ({
          countryCode,
          currencyCode,
          total: {
            amount: (paymentAmountCents / 100).toFixed(2),
            label: paymentLabel,
          },
        })}
        cardTokenizeResponseReceived={async (tokenResult) => {
          console.log("Token Result:", tokenResult);
          const details = (tokenResult as any).details;
          const isApplePay = details?.method === "Apple Pay";
          const paymentMethod = isApplePay ? "apple-pay" : "square";
          if (tokenResult.status !== "OK") {
            toast.error("Card tokenization failed. Please try again.");
            return;
          }
          if (validateBeforePayment && !validateBeforePayment()) return;

          try {
            setLoading(true);

            const res = await createSquarePayment({
              token: tokenResult.token,
              amount: paymentAmountCents,
              ...(hotelId && { hotelId }),
            });

            console.log("Payment Response:", res.data);
            if (res.data.success) {
              const transactionId = res.data?.data?.payment?.id;
              console.log(
                "🔵 SquarePayment - transactionId extracted:",
                transactionId,
              );
              console.log(
                "🔵 SquarePayment - full payment object:",
                res.data?.payment,
              );
              toast.success("Payment successful!");
              handleOrder(paymentMethod, undefined, transactionId);
              console.log(
                "🔵 SquarePayment - handleOrder called with transactionId:",
                transactionId,
              );
              onSuccess?.();
            } else {
              toast.error(getFriendlyPaymentError(res.data.error));
            }
          } catch (err: any) {
            console.error("Payment error:", err);
            toast.error(
              getFriendlyPaymentError(
                err.response?.data?.error || err.response?.data?.errors,
              ),
            );
          } finally {
            setLoading(false);
          }
        }}
      >
        <ApplePay />
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-sm text-gray-400">or pay with card</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>
        <CreditCard
          includeInputLabels
          buttonProps={{
            css: {
              backgroundColor: "var(--main-color, #000)",
              color: "#fff",
              fontSize: "15px",
              fontWeight: "600",
              border: "none",
              borderRadius: "0.375rem",
              padding: "0.75rem",
              cursor: "pointer",
              width: "100%",
              marginTop: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              "&:hover": {
                backgroundColor: "var(--main-color, #000)",
                opacity: 0.95,
              },
              "&:disabled": {
                backgroundColor: "#9ca3af",
                cursor: "not-allowed",
              },
            },
          }}
        >
          {loading ? (
            <>
              <svg
                style={{
                  width: "1.25rem",
                  height: "1.25rem",
                  animation: "spin 1s linear infinite",
                  color: "white",
                }}
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Processing...
            </>
          ) : (
            `PAY ${pricing.toFixed(2)}`
          )}
        </CreditCard>
      </PaymentForm>

      {/* CSS required for Square's iframes - cannot use Tailwind as these are rendered in iframes */}
      <style>{`
        /* Spinner animation */
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Square SDK renders payment fields inside iframes, so we must use CSS with specific IDs */
        /* These selectors target the iframe content which Tailwind classes cannot reach */

        #sq-card-number,
        #sq-cvv,
        #sq-expiration-date,
        #sq-postal-code {
          background: white !important;
          border: 1px solid rgba(0, 0, 0, 0.15) !important;
          border-radius: 8px !important;
          padding: 14px !important;
          font-size: 15px !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
          height: 50px !important;
        }

        .theme-dark #sq-card-number,
        .theme-dark #sq-cvv,
        .theme-dark #sq-expiration-date,
        .theme-dark #sq-postal-code {
          background: #374151 !important;
          border-color: rgba(255, 255, 255, 0.2) !important;
          color: white !important;
        }

        #sq-card-number:focus,
        #sq-cvv:focus,
        #sq-expiration-date:focus,
        #sq-postal-code:focus {
          border-color: var(--main-color, #000) !important;
          box-shadow: 0 0 0 1px var(--main-color, #000) !important;
          outline: none !important;
        }

        .sq-input--exp,
        .sq-input--cvv,
        .sq-input--postal {
          display: inline-block !important;
          width: calc(33.333% - 8px) !important;
          margin-right: 12px !important;
        }

        .sq-input--postal {
          margin-right: 0 !important;
        }

        .sq-label {
          color: #4b5563 !important;
          font-size: 13px !important;
          font-weight: 600 !important;
          margin-bottom: 8px !important;
          display: block !important;
        }

        .theme-dark .sq-label {
          color: #d1d5db !important;
        }

        .sq-error-message {
          color: #ef4444 !important;
          font-size: 12px !important;
          margin-top: 6px !important;
          font-weight: 500 !important;
        }

        .sq-input {
          margin-bottom: 18px !important;
        }

        .sq-input--card-number {
          margin-bottom: 18px !important;
        }

        @media (max-width: 640px) {
          .sq-input--exp,
          .sq-input--cvv,
          .sq-input--postal {
            width: 100% !important;
            margin-right: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}
